import { useQuery } from '@tanstack/react-query'
import { CalendarDays, HandCoins, Lock, ShieldCheck } from 'lucide-react'

import { loanScheduleQueryOptions } from '@/api/loans-api'
import type { LoanCycleDetail, LoanCycleSummary, RepaymentInstalment } from '@/api/loans-types'
import { ErrorSurface } from '@/components/error-surface'
import { NotImplemented } from '@/components/not-implemented'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatShortDate } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'
import { useIsAtLeast } from '@/lib/use-viewport'

import {
  CLOSE_REASON_LABELS,
  FEE_COLLECTION_LABELS,
  INSTALMENT_STATUS_LABELS,
  INTEREST_METHOD_LABELS,
  INTEREST_PERIOD_LABELS,
  MONTH_LENGTH_LABELS,
  REPAYMENT_FREQUENCY_LABELS,
  formatRate,
} from './constants'
import { EmptyPanel, Field, Figure, Section } from './panel-primitives'
import { LOAN_PERMISSIONS, useLoanPermissions } from './permissions'

/**
 * The panels behind the round's tabs.
 *
 * A round is where every figure on this screen lives — the account knows what it is called and
 * nothing about what is owed. The balances therefore sit above the tabs rather than inside one,
 * because they are what somebody opened the screen to read.
 */

/** The outstanding position, in the order a loan officer reads it out. */
export function CycleBalances({ cycle }: { cycle: LoanCycleSummary }) {
  const currency = cycle.currencyCode

  return (
    <div className="grid gap-4 rounded-card border border-border-subtle bg-surface p-4 sm:grid-cols-3 lg:grid-cols-6">
      <Figure label="Principal" >
        <MoneyDisplay value={cycle.principalOutstandingMinor} currency={currency} className="text-left" />
      </Figure>
      <Figure label="Interest">
        <MoneyDisplay value={cycle.interestOutstandingMinor} currency={currency} className="text-left" />
      </Figure>
      <Figure label="Fees">
        <MoneyDisplay value={cycle.feesOutstandingMinor} currency={currency} className="text-left" />
      </Figure>
      <Figure label="Penalty">
        <MoneyDisplay value={cycle.penaltyOutstandingMinor} currency={currency} className="text-left" />
      </Figure>
      <Figure label="Total outstanding" emphasis>
        <MoneyDisplay
          value={cycle.totalOutstandingMinor}
          currency={currency}
          emphasis="strong"
          className="text-left"
        />
      </Figure>
      <Figure label="In arrears" emphasis>
        <div className="space-y-0.5">
          <MoneyDisplay
            value={cycle.amountInArrearsMinor}
            currency={currency}
            emphasis="strong"
            colourNegative
            className="text-left"
          />
          <p className="text-2xs text-muted">
            {cycle.daysInArrears === 0
              ? 'Not behind'
              : `${cycle.daysInArrears} day${cycle.daysInArrears === 1 ? '' : 's'} behind`}
          </p>
        </div>
      </Figure>
    </div>
  )
}

export function CycleOverviewPanel({ cycle }: { cycle: LoanCycleDetail }) {
  const currency = cycle.summary.currencyCode

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Application" description="What was asked for, and why.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Applied for"
            value={
              <MoneyDisplay
                value={cycle.appliedAmountMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
          <Field label="Applied on" value={formatShortDate(cycle.appliedDate)} />
          <Field label="Purpose" value={cycle.purpose} />
          <Field label="Instalments asked for" value={cycle.terms.instalmentCount} />
        </dl>
      </Section>

      <Section title="Appraisal" description="The officer's finding, before any decision.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Notes"
            value={cycle.appraisalNotes}
            hint={cycle.appraisalNotes === null ? 'No appraisal recorded yet.' : undefined}
          />
          <Field
            label="Recommended"
            value={
              cycle.recommendedAmountMinor === null ? null : (
                <MoneyDisplay
                  value={cycle.recommendedAmountMinor}
                  currency={currency}
                  className="text-left"
                />
              )
            }
            hint={
              cycle.recommendedAmountMinor === null && cycle.appraisalNotes !== null
                ? 'The appraiser recommended the amount as applied for.'
                : undefined
            }
          />
          <Field
            label="Bureau enquiry"
            value={cycle.bureauInquiryReference}
            mono
            hint={cycle.bureauInquiryReference === null ? 'No enquiry recorded.' : undefined}
          />
        </dl>
      </Section>

      <Section title="Decision" description="Approval freezes the terms at the approved amount.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Approved"
            value={
              cycle.approvedAmountMinor === null ? null : (
                <MoneyDisplay
                  value={cycle.approvedAmountMinor}
                  currency={currency}
                  className="text-left"
                />
              )
            }
          />
          <Field
            label="Approved on"
            value={cycle.approvedDate === null ? null : formatShortDate(cycle.approvedDate)}
          />
          <Field
            label="Refused because"
            value={cycle.rejectionReason}
            hint={
              cycle.rejectionReason === null
                ? undefined
                : 'This is what the applicant was told.'
            }
          />
        </dl>
      </Section>

      <Section title="Disbursement and closure" description="Where the money went, and how it ended.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Disbursed"
            value={
              cycle.disbursedAmountMinor === null ? null : (
                <MoneyDisplay
                  value={cycle.disbursedAmountMinor}
                  currency={currency}
                  className="text-left"
                />
              )
            }
          />
          <Field
            label="Disbursed on"
            value={
              cycle.summary.disbursementDate === null
                ? null
                : formatShortDate(cycle.summary.disbursementDate)
            }
          />
          <Field label="Disbursement journal" value={cycle.disbursementJournalEntry} mono />
          <Field
            label="Repaid to date"
            value={
              <MoneyDisplay
                value={cycle.totalRepaidMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
          <Field
            label="Closed on"
            value={cycle.closedDate === null ? null : formatShortDate(cycle.closedDate)}
          />
          <Field
            label="Closed because"
            value={cycle.closeReason === null ? null : CLOSE_REASON_LABELS[cycle.closeReason]}
          />
          <Field label="Write-off reference" value={cycle.writeOffReference} mono />
          <Field
            label="Written off"
            value={
              <MoneyDisplay
                value={cycle.writtenOffAmountMinor}
                currency={currency}
                className="text-left"
              />
            }
            hint={
              cycle.recoveredAmountMinor > 0
                ? 'Recoveries below are cash collected after the write-off.'
                : undefined
            }
          />
          <Field
            label="Recovered since"
            value={
              <MoneyDisplay
                value={cycle.recoveredAmountMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
        </dl>
      </Section>
    </div>
  )
}

/**
 * The terms, as they were copied from the product and frozen at approval.
 *
 * They are shown from the round rather than read through to the product on purpose: a product
 * amended next year must not silently reprice a loan somebody signed for this year, and this
 * panel is where an operator can see that it did not.
 */
export function CycleTermsPanel({ cycle }: { cycle: LoanCycleDetail }) {
  const terms = cycle.terms
  const currency = cycle.summary.currencyCode

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Pricing" description="Frozen onto this round. Amending the product cannot reach it.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Method" value={INTEREST_METHOD_LABELS[terms.interestMethod]} />
          <Field
            label="Rate"
            value={`${formatRate(terms.interestRate)} ${INTEREST_PERIOD_LABELS[terms.interestRatePeriod]}`}
          />
          <Field label="Month length" value={MONTH_LENGTH_LABELS[terms.monthLengthConvention]} />
          <Field label="Frequency" value={REPAYMENT_FREQUENCY_LABELS[terms.repaymentFrequency]} />
          <Field label="Instalments" value={terms.instalmentCount} />
          <Field
            label="Grace instalments"
            value={terms.graceInstalments}
            hint="Instalments at the start that carry no principal."
          />
          <Field
            label="Effective interest rate"
            value={
              terms.effectiveInterestRate === null ? null : formatRate(terms.effectiveInterestRate)
            }
            hint={
              terms.effectiveInterestRate === null
                ? 'Not computable before a schedule exists.'
                : 'The figure the pre-agreement disclosure carries.'
            }
          />
          <Field
            label="Nominal annual rate"
            value={terms.nominalAnnualRate === null ? null : formatRate(terms.nominalAnnualRate)}
          />
        </dl>
      </Section>

      <Section title="What it costs" description="Interest and fees, as computed at disbursement.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field
            label="Total interest"
            value={<MoneyDisplay value={terms.totalInterestMinor} currency={currency} className="text-left" />}
          />
          <Field
            label="Total fees"
            value={<MoneyDisplay value={terms.totalFeesMinor} currency={currency} className="text-left" />}
          />
          <Field
            label="Total repayable"
            value={
              <MoneyDisplay
                value={terms.totalRepayableMinor}
                currency={currency}
                emphasis="strong"
                className="text-left"
              />
            }
          />
          <Field
            label="Security deposit required"
            value={
              <MoneyDisplay
                value={terms.securityDepositRequiredMinor}
                currency={currency}
                className="text-left"
              />
            }
          />
          <Field
            label="Compulsory savings"
            value={
              <MoneyDisplay
                value={terms.compulsorySavingsPerInstalmentMinor}
                currency={currency}
                className="text-left"
              />
            }
            hint="Per instalment. Collected with the repayment; not part of what the loan owes."
          />
          <Field
            label="First repayment"
            value={
              terms.firstRepaymentDate === null ? null : formatShortDate(terms.firstRepaymentDate)
            }
            hint={terms.firstRepaymentDate === null ? 'Fixed at disbursement.' : undefined}
          />
          <Field
            label="Maturity"
            value={terms.maturityDate === null ? null : formatShortDate(terms.maturityDate)}
          />
          <Field
            label="Allocation order"
            value={terms.allocationOrder.join(' → ')}
            hint="Oldest instalment first, then this order within it."
          />
        </dl>
      </Section>
    </div>
  )
}

export function CycleFeesPanel({ cycle }: { cycle: LoanCycleDetail }) {
  const currency = cycle.summary.currencyCode

  if (cycle.fees.length === 0) {
    return (
      <EmptyPanel
        icon={<HandCoins className="size-6" />}
        title="No fees on this round"
        description="Fees are copied from the product when the round is priced. This one carries none."
      />
    )
  }

  return (
    <Section title="Fees" description="Charged on this round, at the amounts the product priced.">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fee</TableHead>
              <TableHead>Collection</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Income account</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycle.fees.map((fee) => (
              <TableRow key={fee.id}>
                <TableCell className="font-medium text-heading">{fee.name}</TableCell>
                <TableCell>{FEE_COLLECTION_LABELS[fee.collection]}</TableCell>
                <TableCell>
                  <MoneyDisplay value={fee.amountMinor} currency={currency} />
                </TableCell>
                <TableCell className="font-mono text-xs">{fee.feeIncomeAccountCode}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Section>
  )
}

/**
 * The group guarantee, executed.
 *
 * Each row is one member's security deposit taken against another member's debt, under a signed
 * agreement. It is the most consequential thing that happens on a village-banking loan, so the
 * agreement reference and the reason are shown in full rather than truncated.
 */
export function CycleGuaranteesPanel({ cycle }: { cycle: LoanCycleDetail }) {
  if (cycle.guarantees.length === 0) {
    return (
      <EmptyPanel
        icon={<ShieldCheck className="size-6" />}
        title="No guarantee has been executed"
        description="Nobody else's security deposit has been taken against this round."
      />
    )
  }

  return (
    <Section
      title="Guarantee offsets"
      description="One member's collateral taken for another's debt, under the signed guarantee."
    >
      <div className="space-y-3">
        {cycle.guarantees.map((guarantee) => (
          <div
            key={guarantee.id}
            className="space-y-2 rounded-control border border-border-subtle p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <MoneyDisplay
                value={guarantee.amountMinor}
                currency={guarantee.currencyCode}
                emphasis="strong"
                className="text-left"
              />
              <span className="text-xs text-muted">
                {formatShortDate(guarantee.executedOn)}
              </span>
            </div>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <Field label="Taken from" value={guarantee.sourceCustomerId} mono />
              <Field
                label="Deposit account"
                value={guarantee.sourceSecurityDepositAccountNumber}
                mono
                hint={
                  guarantee.sourceSecurityDepositAccountNumber === null
                    ? 'The Savings module did not name the account.'
                    : undefined
                }
              />
              <Field label="Agreement" value={guarantee.agreementReference} mono />
              <Field label="Reason" value={guarantee.reason} />
            </dl>
          </div>
        ))}
      </div>

      <p className="mt-3 text-2xs text-muted">
        An offset is filed in the audit trail under the guarantee itself, not under the round, so
        it does not appear in the round&apos;s audit trail alongside the appraisal, approval and
        disbursement. This panel is where the sequence of executed guarantees is visible.
      </p>
    </Section>
  )
}

/**
 * Where the round sits in the classification scheme, and whether a person put it there.
 *
 * `classificationSource` is null until a run has reached this round at all, which is a different
 * statement from "performing" and is shown as such.
 */
export function CycleClassificationPanel({ cycle }: { cycle: LoanCycleDetail }) {
  return (
    <Section
      title="Classification"
      description="Set by the nightly run against the scheme in force, or by a countersigned override."
    >
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Class"
          value={
            cycle.summary.classificationCode === null ? null : (
              <StatusBadge status={cycle.summary.classificationCode} />
            )
          }
          hint={
            cycle.summary.classificationCode === null
              ? 'Never classified. That is not the same as performing.'
              : undefined
          }
        />
        <Field
          label="Set by"
          value={
            cycle.classificationSource === null
              ? null
              : cycle.classificationSource === 'Override'
                ? 'A countersigned override'
                : 'The classification run'
          }
        />
        <Field label="Override class" value={cycle.overrideClassCode} />
        <Field
          label="Override reason"
          value={cycle.overrideReason}
          hint={
            cycle.overrideClassCode === null
              ? undefined
              : 'An override is cleared automatically if the facility worsens.'
          }
        />
      </dl>

      {cycle.summary.isRestructured ? (
        <Alert tone="warning" className="mt-4">
          <AlertTitle>This round has been restructured</AlertTitle>
          <AlertDescription>
            A restructured facility is held at the scheme&apos;s floor class for a cooling period
            before it can be reported as performing again.
          </AlertDescription>
        </Alert>
      ) : null}
    </Section>
  )
}

/**
 * The repayment schedule, as it was generated at disbursement.
 *
 * Gated on `loans.schedule.view` separately from `loans.view`. A reader without it is told so;
 * an empty table would read as "no schedule", which is the state of an undisbursed round and
 * means something entirely different.
 */
export function CycleSchedulePanel({ cycle }: { cycle: LoanCycleDetail }) {
  const permissions = useLoanPermissions()
  const isWide = useIsAtLeast('lg')
  const query = useQuery(loanScheduleQueryOptions(cycle.summary.id, permissions.canViewSchedule))

  if (!permissions.canViewSchedule) {
    return (
      <Alert tone="info">
        <Lock aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The schedule is restricted</AlertTitle>
          <AlertDescription>
            Reading the instalments needs{' '}
            <span className="font-mono text-xs">{LOAN_PERMISSIONS.scheduleView}</span>, which your
            role does not hold. The balances above are what the round carries for every reader.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  if (cycle.summary.disbursementDate === null) {
    return (
      <EmptyPanel
        icon={<CalendarDays className="size-6" />}
        title="No schedule yet"
        description="A schedule is generated when the money is released. Until then there are no instalments and no due dates — the terms tab carries what has been agreed."
      />
    )
  }

  if (query.isPending) {
    return <Skeleton className="h-64 w-full" />
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="The schedule could not be read"
      />
    )
  }

  const schedule = query.data
  const currency = schedule.currencyCode

  return (
    <div className="space-y-4">
      <div className="grid gap-4 rounded-card border border-border-subtle bg-surface p-4 sm:grid-cols-3 lg:grid-cols-5">
        <Figure label="Principal">
          <MoneyDisplay value={schedule.principalMinor} currency={currency} className="text-left" />
        </Figure>
        <Figure label="Interest">
          <MoneyDisplay value={schedule.totalInterestMinor} currency={currency} className="text-left" />
        </Figure>
        <Figure label="Fees">
          <MoneyDisplay value={schedule.totalFeesMinor} currency={currency} className="text-left" />
        </Figure>
        <Figure label="Total repayable" emphasis>
          <MoneyDisplay
            value={schedule.totalRepayableMinor}
            currency={currency}
            emphasis="strong"
            className="text-left"
          />
        </Figure>
        <Figure label="Cost of credit" emphasis>
          <div className="space-y-0.5">
            <MoneyDisplay
              value={schedule.totalCostOfCreditMinor}
              currency={currency}
              emphasis="strong"
              className="text-left"
            />
            <p className="text-2xs text-muted">Interest plus fees — the key facts figure.</p>
          </div>
        </Figure>
      </div>

      {isWide ? (
        <div className="overflow-x-auto rounded-card border border-border-subtle bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Principal</TableHead>
                <TableHead className="text-right">Interest</TableHead>
                <TableHead className="text-right">Fees</TableHead>
                <TableHead className="text-right">Savings</TableHead>
                <TableHead className="text-right">Total due</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Penalty</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedule.instalments.map((instalment) => (
                <TableRow key={instalment.instalmentNumber}>
                  <TableCell className="numeric tabular-nums">
                    {instalment.instalmentNumber}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatShortDate(instalment.dueDate)}
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay value={instalment.principalDueMinor} currency={currency} symbol={false} />
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay value={instalment.interestDueMinor} currency={currency} symbol={false} />
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay value={instalment.feeDueMinor} currency={currency} symbol={false} />
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay value={instalment.savingsDueMinor} currency={currency} symbol={false} />
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay
                      value={instalment.totalDueMinor}
                      currency={currency}
                      symbol={false}
                      emphasis="strong"
                    />
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay
                      value={
                        instalment.principalPaidMinor +
                        instalment.interestPaidMinor +
                        instalment.feePaidMinor +
                        instalment.penaltyPaidMinor
                      }
                      currency={currency}
                      symbol={false}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-0.5">
                      <MoneyDisplay
                        value={instalment.penaltyAccruedMinor}
                        currency={currency}
                        symbol={false}
                      />
                      {instalment.penaltyWaivedMinor > 0 ? (
                        <p className="text-2xs whitespace-nowrap text-muted">
                          waived{' '}
                          <MoneyDisplay
                            value={instalment.penaltyWaivedMinor}
                            currency={currency}
                            symbol={false}
                            className="inline"
                          />
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <MoneyDisplay
                      value={instalment.principalBalanceAfterMinor}
                      currency={currency}
                      symbol={false}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <StatusBadge status={instalment.status} />
                    {instalment.paidDate === null ? null : (
                      <p className="mt-0.5 text-2xs text-muted">
                        {formatShortDate(instalment.paidDate)}
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ul className="space-y-3">
          {schedule.instalments.map((instalment) => (
            <li key={instalment.instalmentNumber}>
              <InstalmentCard instalment={instalment} currency={currency} />
            </li>
          ))}
        </ul>
      )}

      <p className="text-2xs text-muted">
        Statuses are the server&apos;s:{' '}
        {Object.values(INSTALMENT_STATUS_LABELS).join(', ').toLowerCase()}. A superseded instalment
        belonged to the schedule a restructure replaced.
      </p>

      {/*
        The figures a key facts statement is made of are all here — total repayable, cost of
        credit, and the effective rate on the terms tab. What is not here is the document, and
        this says so rather than letting an officer believe they have printed one.
      */}
      <NotImplemented
        summary="The pre-agreement disclosure and key facts statement are not produced here: the API publishes the figures they are made of, but no endpoint generates or serves the document, and loans.disclosure.generate authorises nothing this client can call."
        milestone="Phase 1"
        capabilities={[
          'Key facts statement as a printable document, at the figures above',
          'Pre-agreement disclosure carrying the effective interest rate',
          'A loan statement for the member — loans.statement.view has no endpoint either',
        ]}
      />
    </div>
  )
}

/**
 * One instalment as a card, for the narrow layout.
 *
 * The schedule is eleven columns wide at desktop density, and docs/mobile-policy.md rules out
 * answering that with a sideways scroll: a data table becomes stacked cards below `lg`. The
 * instalment number and its due date lead — that is what somebody looks an instalment up by —
 * and every figure keeps its own label rather than relying on a header row that is no longer
 * beside it.
 */
function InstalmentCard({
  instalment,
  currency,
}: {
  instalment: RepaymentInstalment
  currency: string
}) {
  const paid =
    instalment.principalPaidMinor +
    instalment.interestPaidMinor +
    instalment.feePaidMinor +
    instalment.penaltyPaidMinor

  return (
    <div className="rounded-card border border-border-subtle bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-heading">
            Instalment {instalment.instalmentNumber}
          </p>
          <p className="text-2xs text-muted">Due {formatShortDate(instalment.dueDate)}</p>
        </div>
        <div className="text-right">
          <StatusBadge status={instalment.status} />
          {instalment.paidDate === null ? null : (
            <p className="mt-0.5 text-2xs text-muted">
              Settled {formatShortDate(instalment.paidDate)}
            </p>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        <Field
          label="Total due"
          value={
            <MoneyDisplay
              value={instalment.totalDueMinor}
              currency={currency}
              emphasis="strong"
              className="text-left"
            />
          }
        />
        <Field
          label="Paid"
          value={<MoneyDisplay value={paid} currency={currency} className="text-left" />}
        />
        <Field
          label="Principal"
          value={
            <MoneyDisplay
              value={instalment.principalDueMinor}
              currency={currency}
              className="text-left"
            />
          }
        />
        <Field
          label="Interest"
          value={
            <MoneyDisplay
              value={instalment.interestDueMinor}
              currency={currency}
              className="text-left"
            />
          }
        />
        <Field
          label="Fees"
          value={
            <MoneyDisplay
              value={instalment.feeDueMinor}
              currency={currency}
              className="text-left"
            />
          }
        />
        <Field
          label="Savings"
          value={
            <MoneyDisplay
              value={instalment.savingsDueMinor}
              currency={currency}
              className="text-left"
            />
          }
          hint="Collected with the instalment; not part of what the loan owes."
        />
        <Field
          label="Penalty accrued"
          value={
            <MoneyDisplay
              value={instalment.penaltyAccruedMinor}
              currency={currency}
              className="text-left"
            />
          }
          hint={
            instalment.penaltyWaivedMinor > 0
              ? `Waived ${formatMoney(instalment.penaltyWaivedMinor, { currency })}`
              : undefined
          }
        />
        <Field
          label="Balance after"
          value={
            <MoneyDisplay
              value={instalment.principalBalanceAfterMinor}
              currency={currency}
              className="text-left"
            />
          }
        />
      </dl>
    </div>
  )
}
